import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AlertService} from '../../../sdk/service/alert.service';
import {Account, AccountLog, AccountState, WebQRService} from 'web-angular-client';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {AccountStatusUtil} from '../../../sdk/interface/accountStatusUtil';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {saveAs} from 'file-saver';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {ASecureForm} from '../../../sdk/interface/form/ASecureForm';
import {Helper} from '../../../sdk/util/helper';
import {finalize} from 'rxjs';

@Component({
    selector: 'app-qr-code-details',
    templateUrl: './qr-code-details.component.html',
    styleUrls: ['./qr-code-details.component.scss'],
    standalone: false
})
export class QrCodeDetailsComponent extends ASecureForm implements OnInit, OnDestroy
{
    qrCode: Account;
    accountLogs: AccountLog [];
    interval: ReturnType<typeof setInterval> | null;

    constructor(
        authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        private router: Router,
        private route: ActivatedRoute,
        private alertService: AlertService,
        private accountStatusUtil: AccountStatusUtil,
        private webQrService: WebQRService,
        private ngxLoader: NgxUiLoaderService,
        private changeDetectorRef: ChangeDetectorRef)
    {
        super(authenticationService, environmentService);
    }

    get instanceName(): string
    {
        return this.environmentService.instanceName;
    }

    ngOnInit(): void
    {
        this.route.params.subscribe(params =>
        {
            let qrCodeAccountId = params['id'];
            this.loadQrCode(qrCodeAccountId);
            this.loadQrCodeAuditLog(qrCodeAccountId);
        });

        this.interval = setInterval(() =>
        {
            this.loadQrCode(this.qrCode.id);
            this.loadQrCodeAuditLog(this.qrCode.id);
        }, 10000);
    }

    ngOnDestroy(): void
    {
        clearInterval(this.interval);
    }

    returnToQrCodes()
    {
        this.router.navigate([this.routes.SECURE.QR.LIST.path]).then();
    }

    edit()
    {
        this.router.navigate([this.routes.SECURE.QR.EDIT.pathSansParameters, this.qrCode.id]);
    }

    getPayAtNumber(qrCodeUrl: string): string
    {
        return qrCodeUrl.substring(qrCodeUrl.lastIndexOf('/') + 1);
    }

    getAccountStatusUtil(): AccountStatusUtil
    {
        return this.accountStatusUtil;
    }

    downloadQrCode(): void
    {
        this.ngxLoader.start();
        this.webQrService.downloadQr(this.qrCode.id).pipe(finalize(() => this.ngxLoader.stop())).subscribe({
            next: (blob: Blob) =>
            {
                saveAs(blob, `${this.qrCode.description}.pdf`);
            }, error: (error) =>
            {
                this.alertService.error('Failed to download the QR code, please contact support for assistance.');
            }
        });
    }

    getQrCodeStatus(qrCodeState: AccountState): string
    {
        return qrCodeState === AccountState.PAYMENT_OUTSTANDING ?
            'ACTIVE' : (qrCodeState === AccountState.PAYMENT_CANCELLED ||
                qrCodeState === AccountState.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE)
                ? 'CANCELLED' : '';
    }

    private loadQrCode(id: number)
    {
        this.webQrService.getQrAccountById(id).subscribe({
            next: (qrCode: Account) =>
            {
                this.qrCode = qrCode;
                this.qrCode.minimumAmount = Helper.convertToMajorDenomination(qrCode.minimumAmount);
                this.qrCode.maximumAmount = Helper.convertToMajorDenomination(qrCode.maximumAmount);
            },
            error: error =>
            {
                this.alertService.error(error, true);
                this.router.navigate([this.routes.SECURE.QR.LIST.path]).then();
            }
        });
    }

    private loadQrCodeAuditLog(id: number)
    {
        this.webQrService.getQrCodeLogsByAccountId(id).subscribe({
            next: logs =>
            {
                this.accountLogs = logs;
            },
            error: error =>
            {
                this.alertService.error(error, true);
                this.router.navigate([this.routes.SECURE.QR.LIST.path]).then();
            }
        });
    }
}